import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from './Button'

interface Zustand {
  gefallen: boolean
}

/**
 * Fängt einen Fehler beim Rendern der Kinder.
 *
 * ⚠️ Eine Klasse, kein Haken: React bietet das Abfangen bis heute nur über
 * `getDerivedStateFromError` / `componentDidCatch`. Ohne diese Grenze
 * unmountet React bei jedem Fehler den GANZEN Baum — gemessen war `#root`
 * danach leer, also eine weiße Seite ohne Kopfzeile, ohne Navigation und ohne
 * Meldung. Aus Sicht der Familie ist die App dann kaputt, und der einzige Weg
 * zurück ist Neuladen.
 *
 * ⚠️ KEIN eigener Rücksetz-Mechanismus. Der naheliegende Weg wäre ein
 * `schluessel`-Prop, das beim Seitenwechsel zurücksetzt — er wäre toter Code:
 * `motion.main` in der Schale trägt `key={ort.pathname}`, React verwirft den
 * ganzen Teilbaum bei jedem Wechsel und baut die Grenze frisch auf. Das hat
 * die Mutationsprobe gezeigt: der Rücksetzweg liess sich ersatzlos entfernen,
 * ohne dass ein Test rot wurde. Ein Pin in `shell.test.tsx` hält den
 * Schlüssel fest, weil er die Zusicherung trägt.
 */
export class Fehlergrenze extends Component<{ children: ReactNode }, Zustand> {
  override state: Zustand = { gefallen: false }

  static getDerivedStateFromError(): Zustand {
    return { gefallen: true }
  }

  override componentDidCatch(fehler: Error, info: ErrorInfo): void {
    // Kein Meldedienst: der Fehler gehört in die Konsole des Geräts, nicht zu
    // einem Dritten (Spec 6 — Famora spricht mit keinem fremden Server).
    console.error('[famora] Bereich abgestürzt:', fehler, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.gefallen) return this.props.children
    return (
      <div className="fehlerflaeche">
        <h1>Hier ist etwas schiefgegangen</h1>
        <p role="alert">
          Dieser Bereich konnte nicht angezeigt werden. Die übrigen Bereiche funktionieren weiter.
        </p>
        <Button
          onClick={() => {
            this.setState({ gefallen: false })
          }}
        >
          Erneut versuchen
        </Button>
      </div>
    )
  }
}
